import type { ReminderScheduleResponseDto } from '@trellis/api'
import { describeReminderOffset } from '@trellis/roots/data'
import { Label } from '@trellis/roots/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Switch } from '@trellis/roots/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import { calendarDateToUtcMidnight, planInvoiceReminders } from '@trellis/shared'
import { Bell, Info } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'

import { useClientQuery } from '@/hooks/useClients'
import { useOrganizationToday } from '@/hooks/useOrganization'
import {
  useDefaultReminderScheduleQuery,
  useReminderSchedulesQuery,
} from '@/hooks/useReminders'
import { formatCalendarDate } from '@/lib/format'

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
}

/**
 * `invoice` previews the dates from the due date; `recurring` and `split` create several
 * invoices with their own due dates, so they only explain the rule.
 */
export type RemindersSectionVariant = 'invoice' | 'recurring' | 'split'

type PickableSchedule = Pick<
  ReminderScheduleResponseDto,
  'id' | 'name' | 'isDefault' | 'enabled' | 'steps' | 'repeat'
>

interface RemindersSectionProps {
  checked: boolean
  onCheckedChange: (isChecked: boolean) => void
  /** The client being invoiced; their own schedule is offered alongside the global ones. */
  clientId: string | null
  /**
   * The picked schedule. Null means automatic: the client's own schedule if they have
   * one, otherwise the default.
   */
  scheduleId: string | null
  onScheduleChange: (scheduleId: string | null) => void
  /** `yyyy-MM-dd`. Null disables the section, since every reminder is timed from it. */
  dueDate: string | null
  variant?: RemindersSectionVariant
}

const VARIANT_NOTE: Record<RemindersSectionVariant, string> = {
  invoice: 'Reminders start when you email this invoice.',
  recurring:
    'Each invoice this template creates starts its reminders when it is emailed.',
  split: 'Applies to every milestone invoice, starting when each one is emailed.',
}

/**
 * "Enable payment reminders" section on the invoice form, recurring template and quote
 * convert.
 */
export function RemindersSection({
  checked,
  onCheckedChange,
  clientId,
  scheduleId,
  onScheduleChange,
  dueDate,
  variant = 'invoice',
}: RemindersSectionProps) {
  const sectionId = useId()
  const today = useOrganizationToday()
  const { data: clientResult } = useClientQuery(clientId ?? '')
  const { data: globalSchedulesResult } = useReminderSchedulesQuery({ scope: 'global' })
  const { data: defaultScheduleResult } = useDefaultReminderScheduleQuery()

  const client = clientId ? (clientResult?.data ?? null) : null
  const globalSchedules = globalSchedulesResult?.data ?? []
  const clientOwnSchedule =
    client?.appliedReminderSchedule && !client.appliedReminderSchedule.isDefault
      ? client.appliedReminderSchedule
      : null
  const defaultSchedule: PickableSchedule | null =
    globalSchedules.find((schedule) => schedule.isDefault) ??
    defaultScheduleResult?.data ??
    null
  const options: PickableSchedule[] = [
    ...(clientOwnSchedule ? [clientOwnSchedule] : []),
    ...(globalSchedules.length > 0
      ? globalSchedules
      : defaultSchedule
        ? [defaultSchedule]
        : []),
  ]
  const automaticSchedule = clientOwnSchedule ?? defaultSchedule
  const schedule =
    (scheduleId ? options.find((option) => option.id === scheduleId) : undefined) ??
    automaticSchedule

  // A client's own schedule can't follow the invoice to another client.
  const previousClientId = useRef(clientId)
  useEffect(() => {
    if (previousClientId.current === clientId) return
    previousClientId.current = clientId
    const isGlobalPick = globalSchedules.some((option) => option.id === scheduleId)
    if (scheduleId && !isGlobalPick) onScheduleChange(null)
  }, [clientId, globalSchedules, scheduleId, onScheduleChange])

  const needsDueDate = variant === 'invoice' && !dueDate
  const isScheduleOff = schedule !== null && !schedule.enabled
  const isActive = checked && !needsDueDate && !isScheduleOff
  const isPreviewing = isActive && variant === 'invoice' && dueDate !== null && !!schedule

  const planned =
    isPreviewing && dueDate && schedule
      ? planInvoiceReminders({
          dueAt: calendarDateToUtcMidnight(dueDate),
          today: calendarDateToUtcMidnight(today),
          steps: schedule.steps,
          repeat: schedule.repeat,
        }).filter((reminder) => reminder.emailClient)
      : []

  const optionLabel = (option: PickableSchedule) => {
    if (option.id === clientOwnSchedule?.id && client) return `${client.name}'s schedule`
    return option.isDefault ? `${option.name} (default)` : option.name
  }

  return (
    <section
      aria-labelledby={`${sectionId}-title`}
      className="bg-card rounded-xl border shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <Bell className="size-4" aria-hidden="true" />
            <Typography variant="h5" as="h3" id={`${sectionId}-title`}>
              Enable payment reminders
            </Typography>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="About payment reminders"
                >
                  <Info className="size-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Reminders follow the schedule you pick. Unless you choose one, the
                client&apos;s own schedule is used if they have one, otherwise your
                default schedule. {VARIANT_NOTE[variant]} Steps whose date has already
                passed by then are skipped.
              </TooltipContent>
            </Tooltip>
          </div>
          <Typography variant="body-sm" color="muted">
            Automatically email the client before and after the due date.
          </Typography>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Label htmlFor={`${sectionId}-active`}>Active</Label>
          <Switch
            id={`${sectionId}-active`}
            checked={checked && !needsDueDate}
            disabled={needsDueDate}
            onCheckedChange={onCheckedChange}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3.5 px-6 py-5">
        {checked && !needsDueDate && options.length > 0 && schedule && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${sectionId}-schedule`}>Reminder schedule</Label>
            <Select
              value={schedule.id}
              onValueChange={(nextScheduleId) => {
                /* Inside a <form>, Radix mirrors the value into a hidden native select and
                   reports '' when the value and its option arrive in the same render (a
                   client's own schedule loading). '' is never a real pick. */
                if (nextScheduleId) onScheduleChange(nextScheduleId)
              }}
            >
              <SelectTrigger id={`${sectionId}-schedule`} className="sm:max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {optionLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isPreviewing &&
          (planned.length > 0 ? (
            <div className="flex flex-col gap-1">
              <Typography variant="body-sm">Reminder emails go out on:</Typography>
              <ul aria-label="Reminder dates" className="flex flex-col gap-0.5">
                {planned.map((reminder) => (
                  <li key={reminder.scheduledDate.toISOString()}>
                    <Typography variant="body-sm" as="span">
                      {formatCalendarDate(
                        reminder.scheduledDate.toISOString(),
                        SHORT_DATE,
                      )}{' '}
                      · {describeReminderOffset(reminder.daysRelativeToDue)}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Typography variant="body-sm" color="muted">
              Every step is already in the past, so no emails would go out.
            </Typography>
          ))}

        {!isActive && (
          <Typography variant="body-sm" color="muted">
            {needsDueDate
              ? 'Add a due date to turn on reminders. Every reminder is timed from it.'
              : isScheduleOff
                ? 'This schedule is switched off in settings, so no reminders will be sent.'
                : 'No reminders will be sent.'}
          </Typography>
        )}
      </div>
    </section>
  )
}
